import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '@/contexts/AuthContext';
import { AuthForm, AuthMode } from '@/components/auth/AuthForm';
import { BookOpen, Check, DollarSign } from 'lucide-react';

const SubscribeReaderPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, login } = useAuthContext();

  const [mode, setMode] = useState<AuthMode>('register');

  // When authenticated, go straight to readers page
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/readers?setup=reader', { replace: true });
    }
  }, [isAuthenticated]);

  const handleAuthSuccess = (token: string, user: any) => {
    login(token, user);
  };

  // Honest copy: self-serve reader onboarding isn't built yet — readers are
  // set up by the Hey Joe team. This page captures interest, not a promise of
  // instant rates/payouts (payments are currently paused platform-wide).
  const features = [
    'Create your free account to get started',
    'The Hey Joe team sets up your reader profile with you',
    'Get discovered by talent on Hey Joe',
    'Bookings, messages, and availability — managed in one place',
  ];

  // Already authenticated — redirect happening
  if (isAuthenticated) return null;

  return (
    <main
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: 'var(--hj-bg)', color: 'var(--hj-ink)' }}
    >
      <div className="w-full" style={{ maxWidth: 440 }}>
        <div className="text-center" style={{ marginBottom: 24 }}>
          <div
            className="mx-auto flex items-center justify-center"
            style={{
              width: 56,
              height: 56,
              borderRadius: 999,
              background: 'var(--hj-blue-tint)',
              color: 'var(--hj-yellow-ink)',
              marginBottom: 16,
            }}
          >
            <BookOpen className="h-7 w-7" />
          </div>
          <div className="mono-label" style={{ marginBottom: 8, color: 'var(--hj-ink-3)' }}>For readers</div>
          <h1
            className="font-hj-display m-0"
            style={{
              fontSize: 'clamp(32px, 5vw, 44px)',
              lineHeight: 1,
              color: 'var(--hj-ink)',
              letterSpacing: '-0.03em',
              fontWeight: 800,
            }}
          >
            Become a reader.
          </h1>
          <p
            className="m-0"
            style={{
              marginTop: 12,
              color: 'var(--hj-ink)',
              fontSize: 14,
              lineHeight: 1.5,
            }}
          >
            Join the marketplace and start taking sessions — sign-up is free.
          </p>
        </div>

        {/* Feature list */}
        <div
          style={{
            padding: 16,
            borderRadius: 'var(--hj-r-md)',
            background: 'var(--hj-surface-1)',
            border: '1px solid var(--hj-hairline)',
            marginBottom: 16,
          }}
        >
          {features.map((feature) => (
            <div key={feature} className="flex items-center gap-2" style={{ padding: '4px 0', fontSize: 13.5, color: 'var(--hj-ink-2)' }}>
              <Check className="h-4 w-4 flex-shrink-0" style={{ color: 'var(--hj-green)' }} />
              <span>{feature}</span>
            </div>
          ))}
          <div
            style={{
              paddingTop: 8,
              marginTop: 8,
              borderTop: '1px solid var(--hj-hairline-soft)',
            }}
            className="flex items-center justify-center gap-1.5"
          >
            <DollarSign className="h-3.5 w-3.5" style={{ color: 'var(--hj-ink-2)' }} />
            <span style={{ fontSize: 11.5, color: 'var(--hj-ink-2)' }}>
              Free to register — no subscription required
            </span>
          </div>
        </div>

        {/* Auth form */}
        <div
          style={{
            padding: 24,
            borderRadius: 'var(--hj-r-md)',
            background: 'var(--hj-surface-1)',
            border: '1px solid var(--hj-hairline)',
          }}
        >
          <p
            className="text-center m-0"
            style={{ marginBottom: 14, fontSize: 13, color: 'var(--hj-ink-2)', fontWeight: 500 }}
          >
            {mode === 'login' ? 'Sign in to get started' : 'Create an account to get started'}
          </p>
          <AuthForm
            mode={mode}
            onModeChange={setMode}
            onSuccess={handleAuthSuccess}
          />
        </div>
      </div>
    </main>
  );
};

export default SubscribeReaderPage;
